import { z } from 'zod';

import { DeviceDtoScheme } from '~modules/device/model';
import { SectionDtoScheme } from '~modules/section/model';
import { UploadDtoScheme } from '~modules/upload/model';
import { UserDeviceDtoScheme } from '~modules/user-device/model';
import { UserDtoScheme } from '~modules/user/model';

export enum JournalType {
	auto = 'auto',
	manual = 'manual',
}

export enum JournalPrihodUhod {
	prihod = 'prihod',
	uhod = 'uhod',
	empty = '',
}

/**
 * Результат проверки устройства, с которого сделана отметка
 */
export enum JournalDeviceStatus {
	ok = 'ok',
	suspicious = 'suspicious',
	violation = 'violation',
	probable_violation = 'probable_violation',
}

export const translateJournalDeviceStatus = (value: JournalDeviceStatus) => {
	const translations = {
		[JournalDeviceStatus.ok]: '',
		[JournalDeviceStatus.suspicious]: 'Отметка с незакрепленного устройства',
		[JournalDeviceStatus.violation]: 'Отметка с устройства другого сотрудника',
		[JournalDeviceStatus.probable_violation]: 'Вероятно, отметка с устройства другого сотрудника',
	};
	return translations[value];
};

/**
 * Пояснение к статусу: по какому признаку он определен и насколько ему можно верить
 */
export const explainJournalDeviceStatus = (value: JournalDeviceStatus) => {
	const explanations = {
		[JournalDeviceStatus.ok]: '',
		[JournalDeviceStatus.suspicious]:
			'Метка браузера не совпала с закрепленной за сотрудником. Возможные причины: сотрудник сменил телефон, ' +
			'зашел из другого браузера, из приватного режима или очистил данные сайта. Требует проверки.',
		[JournalDeviceStatus.violation]:
			'Точное совпадение: этой же меткой браузера уже отмечался другой сотрудник. ' +
			'Метка хранится в браузере и у двух устройств совпасть не может, ложных срабатываний нет.',
		[JournalDeviceStatus.probable_violation]:
			'Определено по отпечатку устройства, а он у одинаковых моделей телефонов совпадает. ' +
			'Кроме отметки с чужого телефона это может быть: у сотрудника такая же модель, как у коллеги; ' +
			'сотрудник сменил телефон на такую же модель; это его первая отметка с телефона-двойника. ' +
			'Нужна проверка, автоматически считать нарушением нельзя.',
	};
	return explanations[value];
};

export enum JournalErrorType {
	coords_empty = 'coords_empty',
	coords_wrong = 'coords_wrong',
	user_wrong = 'user_wrong',
	device_incognito = 'device_incognito',
	device_unknown = 'device_unknown',
	empty = '',
}

// export const translateJournalPrihodUhod = (value: JournalPrihodUhod) => {
// 	const translations = {
// 		[JournalPrihodUhod.prihod]: 'Приход',
// 		[JournalPrihodUhod.uhod]: 'Уход',
// 	};
// 	return translations[value];
// };

export const translateJournalType = (value: JournalType) => {
	const translations = {
		[JournalType.auto]: 'Регистрация по QR',
		[JournalType.manual]: 'Ручная регистрация',
	};
	return translations[value];
};

export const translateJournalError = (value: JournalErrorType) => {
	const translations = {
		[JournalErrorType.coords_empty]: 'Координаты не заполнены',
		[JournalErrorType.coords_wrong]: 'Некорректные координаты',
		[JournalErrorType.user_wrong]: 'Неверный пользователь',
		[JournalErrorType.device_incognito]: 'Приватный режим браузера',
		[JournalErrorType.device_unknown]: 'Устройство не определено',
		[JournalErrorType.empty]: '',
	};
	return translations[value];
};

export const JournalDtoScheme = z.object({
	id: z.number().describe('Уникальный идентификатор').nullable().optional(),
	date: z
		.string()
		.datetime()
		.describe('Дата записи')
		.transform(val => new Date(val)),
	user_id: z.number().describe('Идентификатор пользователя'),
	user: UserDtoScheme.nullable().optional().describe('Пользователь'),
	section_id: z.number().describe('Идентификатор участка').nullable().optional(),
	section: SectionDtoScheme.nullable().optional().describe('Участок'),
	device_id: z.number().describe('Идентификатор устройства').nullable().optional(),
	device: DeviceDtoScheme.nullable().optional().describe('Устройство'),
	is_part_time: z.boolean().optional().describe('Подработка'),
	type: z.nativeEnum(JournalType).describe('Тип регистрации'),
	prihod_uhod: z.nativeEnum(JournalPrihodUhod).describe('Приход/Уход').nullable().optional(),
	error: z.nativeEnum(JournalErrorType).describe('Ошибка').nullable().optional(),
	qr: z.string().describe('QR код').nullable().optional(),
	device_code: z.string().nullable().optional().describe('Код устройства'),
	user_device_id: z.number().nullable().optional().describe('Идентификатор устройства сотрудника'),
	user_device: UserDeviceDtoScheme.nullable().optional().describe('Устройство сотрудника'),
	ip: z.string().nullable().optional().describe('IP адрес'),
	network_type: z.string().nullable().optional().describe('Тип сети'),
	is_incognito: z.boolean().optional().describe('Отметка из приватного режима браузера'),
	device_status: z.nativeEnum(JournalDeviceStatus).optional().describe('Результат проверки устройства'),
	device_users: z
		.array(z.object({ id: z.number(), name: z.string() }))
		.optional()
		.describe('Кто еще отмечался с этого аппарата'),
	lat: z.string().describe('Широта').nullable().optional(),
	lon: z.string().describe('Долгота').nullable().optional(),
	photo_id: z.number().describe('Идентификатор фото').nullable().optional(),
	photo: UploadDtoScheme.describe('Фото').nullable().optional(),
	manager_id: z.number().describe('Бригадир регистранции').nullable().optional(),
	manager: UserDtoScheme.nullable().optional().describe('Бригадир'),
});

export type JournalDto = z.infer<typeof JournalDtoScheme>;

export const JournalManualDtoSchema = z.object({
	id: z.number().describe('Уникальный идентификатор').nullable().optional(),
	user_id: z.number().describe('Идентификатор пользователя'),
	user: UserDtoScheme.nullable().optional().describe('Пользователь'),
	manager_id: z.number().describe('Бригадир регистранции').nullable().optional(),
	start: z
		.string()
		.datetime()
		.describe('Дата записи')
		.transform(val => new Date(val)),
	end: z
		.string()
		.datetime()
		.describe('Дата конца смены')
		.nullable()
		.optional()
		.transform(val => (val ? new Date(val) : null)),
});

export type JournalManualDto = z.infer<typeof JournalManualDtoSchema>;
